import { linkPitchPosts } from "../entry/pitch-discussion";
import { type Pitch, type PostPublic } from "../desk-records";

export const entryPageSize = 12;

export type PitchCursor = {
  rounds: string | null;
  submissions: string | null;
  posts: string | null;
  episodes: {
    roundId: string;
    completedAt: string;
    cursor: string;
  }[];
};

export type PitchPage = {
  readonly pitches: readonly Pitch[];
  readonly posts: readonly PostPublic[];
  readonly submittedIds: readonly string[];
  readonly observedSubmissionIds: readonly string[];
  readonly signedInRead: boolean;
  readonly nextCursor: PitchCursor | null;
  /** Acknowledged local writes remain in this cache until a submission read observes them. */
  readonly filed?: readonly Pitch[];
};

export function mergePitchPages(pages: readonly PitchPage[]): Pitch[] {
  const pitches = new Map<string, Pitch>();
  const submitted = new Set<string>();

  for (const page of pages) {
    const pageSubmitted = new Set(page.submittedIds);

    for (const pitch of page.pitches) {
      const id = pitch.policyVersionId;
      const previous = pitches.get(id);

      pitches.set(
        id,
        reconcilePitch({
          previous,
          incoming: pitch,
          previousSubmitted: submitted.has(id),
          incomingSubmitted: pageSubmitted.has(id),
        }),
      );
    }

    for (const id of page.submittedIds) submitted.add(id);
  }

  for (const pitch of pages.flatMap((page) => page.filed ?? [])) {
    const previous = pitches.get(pitch.policyVersionId);
    pitches.set(pitch.policyVersionId, {
      ...pitch,
      grade: previous?.grade ?? pitch.grade,
    });
    submitted.add(pitch.policyVersionId);
  }

  const signedInRead = pages.some((page) => page.signedInRead);
  const visible = [...pitches.values()].filter(
    (pitch) => !signedInRead || submitted.has(pitch.policyVersionId),
  );
  return linkPitchPosts({
    pitches: visible,
    posts: pages.flatMap((page) => page.posts),
  });
}

function reconcilePitch({
  previous,
  incoming,
  previousSubmitted,
  incomingSubmitted,
}: {
  readonly previous: Pitch | undefined;
  readonly incoming: Pitch;
  readonly previousSubmitted: boolean;
  readonly incomingSubmitted: boolean;
}): Pitch {
  if (!previous) return incoming;

  const newer = incoming.filedAt > previous.filedAt;
  const useIncoming = incomingSubmitted
    ? !previousSubmitted || newer
    : !previousSubmitted && newer;
  const newestGrade =
    incoming.grade &&
    (!previous.grade || incoming.grade.gradedAt > previous.grade.gradedAt)
      ? incoming.grade
      : previous.grade;
  return { ...(useIncoming ? incoming : previous), grade: newestGrade };
}
